import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, CircleAlert, Languages } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { catalogEntries } from './catalogEntries'

type Target = 'ru' | 'uz'

/**
 * A22.05 — Yetishmayotgan tarjimalar. Evristika: qiymati inglizcha (en) bilan
 * AYNAN bir xil bo'lsa "tarjimasiz" (atoqli otlar yolg'on-musbat bo'lishi mumkin).
 * Funksiya-qiymatlar (parametrli) chiqarib tashlanadi. Backend YO'Q (client-side).
 */
export function LocalizationMissing() {
  const t = useT()
  const [lang, setLang] = useState<Target>('ru')
  const all = useMemo(() => catalogEntries(), [])
  const missing = useMemo(
    () => all.filter((e) => !e.fn && e.en !== '' && e[lang] === e.en),
    [all, lang],
  )

  return (
    <>
      <PageHeader
        title={t.loc.missingTitle}
        screenId="A22.05"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.loc.title}
          </Link>
        }
      />
      <div className="flex items-center gap-2 border-b border-border-subtle p-3">
        <Languages size={15} className="text-tertiary" />
        {(['ru', 'uz'] as Target[]).map((l) => (
          <button
            key={l}
            onClick={() => setLang(l)}
            className={
              'rounded-md px-3 py-1 text-sm ' +
              (lang === l ? 'bg-accent text-white' : 'bg-surface-2 text-secondary hover:text-primary')
            }
          >
            {l.toUpperCase()}
          </button>
        ))}
        <span className="ml-2 text-sm text-tertiary">
          {missing.length.toLocaleString()} / {all.length.toLocaleString()}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <p className="mb-2 text-xs text-tertiary">{t.loc.heuristic}</p>
        <div className="overflow-hidden rounded-md border border-border-default">
          <div className="grid grid-cols-[minmax(160px,1fr)_1fr] gap-3 border-b border-border-default bg-surface-2 px-3 py-2 text-xs font-medium text-tertiary">
            <span>{t.loc.keys}</span>
            <span>EN ({t.loc.value})</span>
          </div>
          {missing.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-tertiary">
              <CircleAlert size={22} className="mx-auto mb-2 opacity-60" />
              {t.loc.allTranslated}
            </div>
          ) : (
            missing.map((e) => (
              <div
                key={e.key}
                className="grid grid-cols-[minmax(160px,1fr)_1fr] gap-3 border-b border-border-subtle px-3 py-1.5 text-sm last:border-0"
              >
                <span className="truncate font-mono text-xs text-accent" title={e.key}>
                  {e.key}
                </span>
                <span className="truncate text-secondary" title={e.en}>{e.en}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}
